/**
 * Automation errors in plain words (2026-10-22: "error not show like that, users are confused - show it user friendly,
 * all places").
 *
 * What the automation stores is written for whoever debugs it: "[Reliance] [E309] The step did not finish ...",
 * "invalid session id", "Wait timed out after 10000ms ... By(css selector, *[id="ChkBox24"])", error types such as
 * "PostSubmissionError" and stages such as "post-calculation". Every screen that shows an automation error goes through
 * friendlyError() instead, so the operator reads WHAT happened and WHAT TO DO, the same way everywhere:
 *   Error Logs page, View page status card, the list's part hover card, the History dialog, the PDF, the live toasts.
 * The original text stays available (`technical`) for the Policy Issues page's "Details for support".
 *
 * Display only: works the same for records saved long ago - nothing stored is changed.
 */

const RULES = [
  {
    key: "held",
    test: /held by the operator|hold was pressed/i,
    title: "Stopped by Hold",
    message: "Someone pressed Hold, so the automation stopped at a safe point.",
    action: "Press Release to continue from the step it stopped at.",
  },
  {
    key: "noLogin",
    test: /\[E205\]|no (active )?.{0,40}portal credentials|no portal credentials|credentials (are )?not (configured|found)/i,
    title: "No insurer login saved",
    message: "There is no portal user name and password for this insurer.",
    action: "Add the insurer login in Account & Policy Settings (or on the user), then run the policy again.",
  },
  {
    key: "locked",
    test: /\[E204\]|account (is )?locked|user (is )?locked/i,
    title: "Insurer login is locked",
    message: "The insurer website has locked this login.",
    action: "Unlock the login with the insurer, then run the policy again.",
  },
  {
    key: "badLogin",
    test: /\[E203\]|invalid credentials|wrong password|incorrect (user ?name|password)|authentication failed|invalid (user ?name|password)/i,
    title: "Insurer login was refused",
    message: "The insurer website did not accept the user name or password.",
    action: "Check the insurer login in Account & Policy Settings, then run the policy again.",
  },
  {
    key: "captcha",
    test: /captcha|\[E202\]/i,
    title: "Could not read the login captcha",
    message: "The picture code on the insurer's login page could not be read.",
    action: "This is usually fixed by trying again - it is retried automatically.",
  },
  {
    key: "stuck",
    test: /\[E309\]|\[E302\]|stopped responding|did not finish in|took too long|timed? ?out|timeout/i,
    title: "Insurer website did not respond",
    message: "The insurer website was too slow or stopped working, so this attempt was stopped.",
    action: "It is tried again automatically. If it keeps happening, check that the insurer website is working.",
  },
  {
    key: "browser",
    test: /invalid session id|session deleted|chrome not reachable|renderer|crashed|target window|disconnected|session not created|\[E500\]/i,
    title: "The browser closed unexpectedly",
    message: "The automation's browser window stopped in the middle of the work.",
    action: "It is tried again automatically.",
  },
  {
    key: "network",
    test: /econnrefused|enotfound|econnreset|socket hang up|net::err|network|\[E502\]/i,
    title: "Network problem",
    message: "The insurer website could not be reached.",
    action: "It is tried again automatically. Check the internet connection if it keeps happening.",
  },
  {
    key: "kyc",
    test: /\bkyc\b|aadhaar|aadhar|ovd/i,
    title: "KYC was not completed",
    message: null, // the insurer's own words are the useful part - kept
    action: "Check the customer's KYC documents, then run the policy again.",
  },
  {
    key: "duplicate",
    test: /duplicate|already exists|\[E103\]/i,
    title: "Already done at the insurer",
    message: null,
    action: "Check the insurer website before running the policy again.",
  },
  {
    key: "page",
    test: /element|not found|did not appear|could not be found|could not find|intercepted|stale|popup|overlay|field\/button|not clickable/i,
    title: "Insurer page did not load as expected",
    message: null, // our sentence already names the field ("Could not find the 'PAN Not Available' checkbox.")
    action: "This is usually fixed by trying again - it is retried automatically.",
  },
];

const AFTER_SAVE_ACTION =
  "The proposal may already be saved at the insurer, so it is NOT tried again automatically. Check the policy on the insurer website before running it again.";

/** Codes, insurer tags, Selenium noise and stack traces taken off; one readable sentence or two. */
export function cleanErrorText(raw) {
  let text = String(raw == null ? "" : raw);
  text = text.split(/\n\s*(at |Build info|System info|Driver info|\(Session info)/)[0];
  text = text.replace(/\(Session info[^)]*\)/gi, "");
  text = text.replace(/\{"method":[^}]*\}/g, "");
  text = text.replace(/By\([^)]*\)|By\.[a-zA-Z]+: [^\s,]+/g, "");
  text = text.replace(/^\s*(\[[^\]]{1,24}\]\s*)+/g, ""); // [Reliance] [E309] ...
  text = text.replace(/\[(E\d{3})\]\s*/g, "");
  text = text.replace(/^(Error|Critical Error|TimeoutError|WebDriverError|NoSuchElementError)\s*[:-]\s*/i, "");
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

/** The step it was stuck at, when the stall message names it ("... after "Clicking 'Get Coverage Details' button..." -"). */
const stuckStep = (text) => {
  const m = String(text || "").match(/after "([^"]{3,140})"/);
  return m ? m[1].replace(/\.\.\.$/, "").trim() : "";
};

const isAfterSave = ({ errorType, stage, errorCode } = {}) =>
  errorType === "PostSubmissionError" ||
  stage === "post-submission" ||
  stage === "post-calculation" ||
  errorCode === "E401";

/**
 * @param {string} raw   the stored error text
 * @param {Object} [ctx] { errorType, stage, errorCode } when the screen has them
 * @returns {{ key, title, message, action, technical }}
 */
export function friendlyError(raw, ctx = {}) {
  const technical = String(raw == null ? "" : raw).trim();
  const cleaned = cleanErrorText(technical);
  const rule = RULES.find((r) => r.test.test(technical));
  const afterSave = isAfterSave(ctx);

  if (!technical) {
    return { key: "none", title: "The automation stopped", message: "No reason was recorded.", action: "", technical };
  }

  let message;
  if (!rule) message = cleaned;
  else if (rule.key === "stuck") {
    const step = stuckStep(technical);
    message = step ? `${rule.message} It was waiting at "${step}".` : rule.message;
  } else message = rule.message || cleaned;

  return {
    key: rule ? rule.key : "other",
    title: rule ? rule.title : "The automation stopped",
    message,
    action: afterSave ? AFTER_SAVE_ACTION : rule ? rule.action : "Check the details, then run the policy again.",
    technical,
  };
}

/** One line for small places (hover cards, toasts, PDF): "Title - message". */
export function friendlyErrorLine(raw, ctx) {
  const f = friendlyError(raw, ctx);
  return f.message ? `${f.title} - ${f.message}` : f.title;
}

const TYPE_LABELS = {
  PostSubmissionError: "After the proposal was saved",
  LoginFormError: "While logging in / filling the form",
  AutomationIncomplete: "Stopped on purpose",
  SystemError: "System problem",
  HydrationError: "Preparing the policy data",
  ValidationError: "Policy data problem",
  BriskError: "Brisk certificate",
  PostCompletionWarning: "Done, with a warning",
  Error: "The automation stopped",
};

/** "PostSubmissionError" -> "After the proposal was saved". */
export function errorTypeLabel(type) {
  if (!type || type === "Unknown") return "Not recorded";
  return TYPE_LABELS[type] || humanize(type.replace(/Error$/, ""));
}

const STAGE_LABELS = {
  "login-form": "Login or form filling",
  login: "Login",
  "post-submission": "After the proposal was saved",
  "post-calculation": "After the premium was calculated",
  exception: "Unexpected stop",
  hydration: "Preparing the policy data",
  validation: "Checking the policy data",
  "resume-proposal-page": "Opening the saved proposal",
  "operator-hold": "Held by the operator",
  brisk: "Brisk certificate",
  proposal: "Proposal",
  kyc: "KYC",
  payment: "Payment",
};

/** "post-calculation" -> "After the premium was calculated"; anything unknown in plain words. */
export function stageLabel(stage) {
  if (!stage || stage === "Unknown") return "Not recorded";
  return STAGE_LABELS[stage] || humanize(stage);
}

function humanize(s) {
  const words = String(s)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The context a job PART carries (list rows, View page, PDF): its stage, error code and whether it failed after saving. */
export const partErrorContext = (part = {}) => ({
  stage: part.stage,
  errorCode: part.lastErrorCode,
  errorType: part.status === "failed_post_submission" ? "PostSubmissionError" : undefined,
});
